import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	type DialogProps,
	DialogTitle,
	TextField,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Snackbar from "@material-ui/core/Snackbar";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import { type SyntheticEvent, useEffect, useMemo, useState } from "react";
import { deleteWorkOrder } from "../../../pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "../context/work-order-modals.context";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";

const moment = getMomentTz();

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	data: ModalWorkOrder | null;
	suffixTitle?: string;
};

export function DeleteWorkOrderModal({
	show,
	onHide,
	data,
	suffixTitle,
}: Props) {
	const classes = useStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [deleteSuccess, setDeleteSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");

	const { state, actions } = useWorkOrderModalsContext();
	const {
		state: {
			modalWorkOrderSelected: {
				Comments,
				TemplateTitle,
				WorkOrderPlannedHours,
				WorkOrderNumberOfWorkers,
				WorkOrderOpenedBy,
				OpenTimestamp,
				WorkOrderName,
				AssetName,
				Title,
			},
		},
		actions: { setAssetName },
	} = useWorkOrderModalsContext();
	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");
	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});
	//functions
	const handleCancel = () => {
		onHide(false);
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setDeleteSuccess(false);
	};

	const formatDescription = (description: string): string => {
		if (description?.includes("/") && description.split("/").length > 3) {
			const parts = description.split("/");
			let formattedDescription = "";

			for (let i = 0; i < parts.length; i++) {
				if (i < parts.length - 2) {
					formattedDescription += ".../";
				} else if (i === parts.length - 2) {
					if (parts[i].length > 6) {
						formattedDescription += parts[i].substring(0, 6) + ".../";
					} else {
						formattedDescription += parts[i] + "/";
					}
				} else {
					formattedDescription += parts[i];
					if (i < parts.length - 1) {
						formattedDescription += "/";
					}
				}
			}

			return formattedDescription;
		} else {
			return description;
		}
	};

	const handleDeleteBtn = async () => {
		setIsSubmitLoading(true);
		const res = await deleteWorkOrder(
			state.modalWorkOrderSelected as WorkOrder,
		);
		if (res.ok) {
			setIsSubmitLoading(false);
			setOpenDeleteDialog(false);
			setTimeout(() => {
				setDeleteSuccess(true);
				onHide(true);
			}, 100);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			actions.setComments(data?.Comments);
			actions.setTemplateTitle(data?.TemplateTitle);
			actions.setWorkOrderPlannedHours(data?.WorkOrderPlannedHours);
			actions.setWorkOrderNumberOfWorkers(data?.WorkOrderNumberOfWorkers);
			actions.setWorkOrderOpenedBy(data?.WorkOrderOpenedBy);
			actions.setOpenTimestamp(data?.OpenTimestamp);
			actions.setWorkOrderName(data?.WorkOrderName);
			GetAssetFullPath((data?.AssetId as number)?.toString());
			actions.setTitle(data?.Title);
			actions.setAssetName(data?.AssetName);
			actions.setWorkOrderId(data?.WorkOrderId);
		}
		setIsSubmitLoading(false);
	}, [show]);

	const description = useMemo(
		() => formatDescription(AssetName as string),
		[AssetName],
	);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete Work Order${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<Grid container>
					<Grid item md={12} xs={12}>
						<DialogTitle
							style={{
								padding: "0 24px",
								width: "100%",
							}}
						>
							<h1 style={{ fontSize: "1.3em" }}>DELETE: {Title}</h1>
						</DialogTitle>
					</Grid>
					<Grid container item>
						<DialogContent dividers>
							<Grid
								item
								md={12}
								xs={12}
								style={{ padding: "0px", marginTop: "-5px" }}
							>
								<TextField
									label="Work order"
									variant="outlined"
									disabled
									value={WorkOrderName}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid>
							<Grid
								item
								md={12}
								xs={12}
								style={{ padding: "0px", marginTop: 10 }}
							>
								<TextField
									label="Asset"
									variant="outlined"
									disabled
									value={description || ""}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid>
							<Grid container item md={12} xs={12}>
								<Grid
									item
									md={6}
									xs={6}
									style={{ padding: "0 10px 0 0", marginTop: 10 }}
								>
									<TextField
										label="Opened by"
										variant="outlined"
										disabled
										value={WorkOrderOpenedBy}
										fullWidth
										margin="dense"
										autoComplete="off"
									/>
								</Grid>
								<Grid
									item
									md={6}
									xs={6}
									style={{ padding: "0 0 0 10px", marginTop: 10 }}
								>
									<TextField
										label="Opened"
										variant="outlined"
										disabled
										value={moment(OpenTimestamp).format("DD/MM/YYYY HH:mm A")}
										fullWidth
										margin="dense"
										autoComplete="off"
									/>
								</Grid>
							</Grid>
							{TemplateTitle ? (
								<Grid container>
									<Grid
										item
										md={12}
										xs={12}
										style={{ padding: "0px", marginTop: 10 }}
									>
										<TextField
											label="Original Title"
											variant="outlined"
											disabled
											value={TemplateTitle}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid
										item
										md={6}
										xs={6}
										style={{ padding: "0 10px 0 0px", marginTop: 10 }}
									>
										<TextField
											label="Planned Hours:"
											variant="outlined"
											disabled
											value={WorkOrderPlannedHours}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid
										item
										md={6}
										xs={6}
										style={{ padding: "0 0 0 10px", marginTop: 10 }}
									>
										<TextField
											label="Number of Workers"
											variant="outlined"
											disabled
											value={WorkOrderNumberOfWorkers}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
								</Grid>
							) : (
								""
							)}
							<Grid container justifyContent="flex-end">
								<Grid
									item
									md={12}
									xs={12}
									style={{
										marginBottom: "10px",
										marginTop: "22px",
										padding: "0px",
									}}
								>
									<Card
										variant="outlined"
										style={{ backgroundColor: "#E9F2F8" }}
										className={classes.root}
									>
										<CardContent>
											<Typography className={classes.messageTitle}>
												Comments:
											</Typography>
											<SimpleTextAreaControl
												value={Comments || ""}
												disabled
												showColor={false}
												styleText={{
													height: "80px",
													border: "none",
													backgroundColor: "#E9F2F8",
													fontSize: 16,
												}}
											/>
										</CardContent>
									</Card>
								</Grid>
							</Grid>
						</DialogContent>
					</Grid>
					<Grid
						item
						md={12}
						xs={12}
						justifyContent="flex-end"
						style={{ marginTop: "15px" }}
					>
						<DialogActions style={{ padding: "0 10px" }}>
							<Grid
								item
								md={3}
								xs={12}
								style={{ margin: 0, padding: "0px 10px 14px" }}
							>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={handleCancel}
								>
									CANCEL
								</Button>
							</Grid>
							<Grid
								item
								md={3}
								xs={12}
								style={{ margin: 0, padding: "0px 10px 14px" }}
							>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="secondary"
									onClick={() => setOpenDeleteDialog(true)}
								>
									Delete
								</Button>
							</Grid>
						</DialogActions>
					</Grid>
				</Grid>
			</Dialog>
			<Dialog
				open={openDeleteDialog}
				onClose={() => setOpenDeleteDialog(false)}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
				<DialogContent>
					<DialogContentText style={{ padding: 0, margin: 0 }}>
						Be careful, once deleted you will not be able to recover this work
						order, are you sure you want to delete it?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setOpenDeleteDialog(false)}
						autoFocus
						color="default"
					>
						CANCEL
					</Button>
					<Button
						autoFocus
						color="secondary"
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						onClick={handleDeleteBtn}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={deleteSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="error" onClose={handleClose}>
					Order deleted
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
